import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'unresolved', label: 'Unresolved' },
  { key: 'resolved', label: 'Resolved' },
];

function Stars({ rating }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ color: i <= rating ? '#f59e0b' : '#d4d1cc', fontSize: 14 }}>&#9733;</span>
      ))}
    </span>
  );
}

function formatDate(dateString) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function FeedbackCard({ feedback, onResolve }) {
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    try {
      await onResolve(feedback.id);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="card" style={{
      padding: '20px 24px',
      marginBottom: 12,
      borderLeft: feedback.is_resolved
        ? '4px solid var(--green)'
        : '4px solid #f59e0b',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Stars rating={feedback.rating} />
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 4,
              background: feedback.is_resolved ? 'rgba(22,163,74,0.1)' : 'rgba(245,158,11,0.1)',
              color: feedback.is_resolved ? '#16a34a' : '#d97706',
            }}>
              {feedback.is_resolved ? 'Resolved' : 'Unresolved'}
            </span>
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            {formatDate(feedback.created_at)}
          </div>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={handleToggle}
          disabled={toggling}
          style={{
            fontSize: 12,
            color: feedback.is_resolved ? '#6b7280' : '#16a34a',
          }}
        >
          {toggling ? '...' : feedback.is_resolved ? 'Reopen' : 'Mark Resolved'}
        </button>
      </div>

      {feedback.feedback_text && (
        <p style={{ fontSize: 14, color: '#1a1a1a', margin: '10px 0', lineHeight: 1.6 }}>
          "{feedback.feedback_text}"
        </p>
      )}

      {!feedback.feedback_text && (
        <p style={{ fontSize: 14, color: '#9ca3af', margin: '10px 0', fontStyle: 'italic' }}>
          (No message - rating only)
        </p>
      )}

      {/* Contact info */}
      {(feedback.customer_name || feedback.customer_email || feedback.customer_phone) && (
        <div style={{
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
          marginTop: 10,
          padding: '10px 12px',
          background: '#faf9f6',
          borderRadius: 8,
          fontSize: 13,
          color: '#374151',
        }}>
          {feedback.customer_name && (
            <span><strong>Name:</strong> {feedback.customer_name}</span>
          )}
          {feedback.customer_email && (
            <span>
              <strong>Email:</strong>{' '}
              <a href={`mailto:${feedback.customer_email}`} style={{ color: '#16a34a', textDecoration: 'none' }}>
                {feedback.customer_email}
              </a>
            </span>
          )}
          {feedback.customer_phone && (
            <span>
              <strong>Phone:</strong>{' '}
              <a href={`tel:${feedback.customer_phone}`} style={{ color: '#16a34a', textDecoration: 'none' }}>
                {feedback.customer_phone}
              </a>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function FeedbackPage() {
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [toast, setToast] = useState(null);

  const showToast = (message, isError = false) => {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = useCallback(async () => {
    try {
      const [feedback, feedbackStats] = await Promise.all([
        api.get('/feedback'),
        api.get('/feedback/stats'),
      ]);
      setFeedbackList(feedback);
      setStats(feedbackStats);
    } catch (err) {
      showToast('Failed to load feedback', true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResolve = async (feedbackId) => {
    try {
      const updated = await api.post(`/feedback/${feedbackId}/resolve`, {});
      setFeedbackList(prev => prev.map(f => f.id === feedbackId ? updated : f));
      // Update stats
      const newStats = await api.get('/feedback/stats');
      setStats(newStats);
      showToast(updated.is_resolved ? 'Marked as resolved' : 'Reopened');
    } catch (err) {
      showToast('Failed to update: ' + err.message, true);
    }
  };

  const filteredFeedback = activeTab === 'all'
    ? feedbackList
    : activeTab === 'unresolved'
      ? feedbackList.filter(f => !f.is_resolved)
      : feedbackList.filter(f => f.is_resolved);

  const getCounts = (key) => {
    if (key === 'all') return feedbackList.length;
    if (key === 'unresolved') return feedbackList.filter(f => !f.is_resolved).length;
    return feedbackList.filter(f => f.is_resolved).length;
  };

  if (loading) return <div className="loading">Loading feedback...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Private Feedback</h1>
          <p className="page-subtitle">
            Feedback captured from customers before they leave a public negative review
          </p>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>INTERCEPTED</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#16a34a' }}>{stats.intercepted}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>negative reviews prevented</div>
          </div>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>UNRESOLVED</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: stats.unresolved > 0 ? '#f59e0b' : '#16a34a' }}>
              {stats.unresolved}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>need attention</div>
          </div>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>THIS MONTH</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#1a1a1a' }}>{stats.this_month}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>new feedback</div>
          </div>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>RESOLVED</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#16a34a' }}>{stats.resolved}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>issues addressed</div>
          </div>
        </div>
      )}

      {/* Info banner - show when no feedback yet */}
      {feedbackList.length === 0 && (
        <div className="card" style={{
          padding: '24px',
          marginBottom: 20,
          background: 'rgba(22,163,74,0.04)',
          borderColor: 'rgba(22,163,74,0.15)',
        }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', margin: '0 0 8px' }}>
            How the Feedback Funnel works
          </h3>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px', lineHeight: 1.6 }}>
            When customers visit your review link, they're first asked to rate their experience.
            Happy customers (4-5 stars) are redirected straight to your Google review page.
            Unhappy customers (1-3 stars) see a private feedback form instead - so their complaint
            comes to you, not Google.
          </p>
          <p style={{ fontSize: 13, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
            Share your review link on receipts, in emails, or via the QR code in Settings.
            Private feedback will appear here as it comes in.
          </p>
        </div>
      )}

      {/* Tabs */}
      {feedbackList.length > 0 && (
        <>
          <div className="filter-tabs">
            {TABS.map(tab => (
              <button
                key={tab.key}
                className={`filter-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                <span className="count">{getCounts(tab.key)}</span>
              </button>
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            {filteredFeedback.length === 0 ? (
              <div className="empty-state card">
                <div className="empty-state-icon">&#10003;</div>
                <div className="empty-state-text">
                  {activeTab === 'unresolved'
                    ? 'All feedback has been resolved!'
                    : `No ${activeTab} feedback.`}
                </div>
              </div>
            ) : (
              filteredFeedback.map(fb => (
                <FeedbackCard
                  key={fb.id}
                  feedback={fb}
                  onResolve={handleResolve}
                />
              ))
            )}
          </div>
        </>
      )}

      {toast && (
        <div className={`toast ${toast.isError ? 'error' : ''}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
